import { useMemo } from 'react';

const QUESTION_GROUP_KEYS = ['productInformation', 'packagingStorage'];
const GRID_PRODUCT_LIMIT = 15;

const getRecommendationSubsteps = (productCount) => {
  if (!productCount) {
    // No productCount yet - show both
    return [
      { key: 'recommendTemplate', title: 'Template' },
      { key: 'recommendGrid', title: 'Grid View' },
    ];
  }

  if (productCount > GRID_PRODUCT_LIMIT) {
    // More than 15 products: show only Template
    return [{ key: 'recommendTemplate', title: 'Template' }];
  }

  // 15 or fewer products: show only Grid View
  return [{ key: 'recommendGrid', title: 'Grid View' }];
};

export const useStepGroups = ({ isBypassed, isTerminal, productCount }) => {
  return useMemo(() => {
    // Define all possible steps
    const allSteps = [
      {
        key: 'intro',
        title: 'Introduction',
        stepKey: 'intro',
        type: 'single',
      },
      {
        key: 'productInformation',
        title: 'Product Information',
        type: 'group',
        substeps: [
          { key: 'productCount', title: 'Product Count' },
          { key: 'foodService', title: 'Foodservice / Bulk' },
          { key: 'alcoholic', title: 'Alcoholic' },
        ],
      },
      {
        key: 'packagingStorage',
        title: 'Packaging & Storage',
        type: 'group',
        substeps: [
          { key: 'retailInnerPack', title: 'Retail Inner Pack' },
          { key: 'storeDisplay', title: 'Store Display / Shipper' },
          { key: 'merchandisedInTray', title: 'Merchandised in Tray' },
          { key: 'multipack', title: 'Multipack' },
          { key: 'perishable', title: 'Perishable' },
        ],
      },
      // contactSales - ONLY show when isTerminal
      ...(isTerminal
        ? [
            {
              key: 'contactSales',
              title: 'Contact Sales',
              stepKey: 'contactSales',
              type: 'single',
            },
          ]
        : []),
      {
        key: 'recommendation',
        title: 'Recommendation',
        type: 'group',
        substeps: getRecommendationSubsteps(productCount),
      },
      // BYPASS STEPS - only show when bypassed
      ...(isBypassed
        ? [
            {
              key: 'productGridQuestion',
              title: 'Submission Method',
              stepKey: 'productGridQuestion',
              type: 'single',
            },
          ]
        : []),
      {
        key: 'success',
        title: 'Complete',
        stepKey: 'success',
        type: 'single',
      },
    ];

    // Terminal filter (highest priority)
    if (isTerminal) {
      return allSteps.filter((step) => {
        return (
          step.key === 'intro' ||
          QUESTION_GROUP_KEYS.includes(step.key) ||
          step.key === 'contactSales'
        );
      });
    }

    // Bypass filter
    if (isBypassed) {
      return allSteps.filter(
        (step) =>
          !QUESTION_GROUP_KEYS.includes(step.key) &&
          step.key !== 'recommendation' &&
          step.key !== 'contactSales' // Never show contactSales in bypass mode
      );
    }

    // The template path skips every question after Product Count
    if (productCount > GRID_PRODUCT_LIMIT) {
      return allSteps
        .filter(
          (step) => step.key !== 'success' && step.key !== 'packagingStorage'
        )
        .map((step) =>
          step.key === 'productInformation'
            ? {
                ...step,
                substeps: step.substeps.filter(
                  (substep) => substep.key === 'productCount'
                ),
              }
            : step
        );
    }

    // Normal flow - exclude contactSales and bypass-only steps
    return allSteps.filter(
      (step) =>
        step.key !== 'contactSales' &&
        step.key !== 'productGridQuestion'
    );
  }, [isBypassed, isTerminal, productCount]);
};

export default useStepGroups;
